import React from 'react';
import {Box, IconButton, MenuItem, Select, TextField, Typography, FormControlLabel, Switch, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {DropdownQuestion, Option} from "crqlar-react-common/dist/models/forms/form";
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {useTranslation} from "react-i18next";

interface DropdownInputProps {
  question: DropdownQuestion;
  onQuestionUpdate: (question: DropdownQuestion) => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 15);

const DropdownInput: React.FC<DropdownInputProps> = ({
  question,
  onQuestionUpdate,
  isSelected = false,
  onSelect
}) => {
  const {t} = useTranslation();

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      title: e.target.value
    });
  };

  const handleDescriptionChange = (value?: string) => {
    onQuestionUpdate({
      ...question,
      description: value || ''
    });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      required: e.target.checked
    });
  };

  const handleOptionChange = (optionId: string, newLabel: string) => {
    const updatedOptions = (question.options || []).map(option =>
      option.id === optionId ? {...option, label: newLabel} : option
    );
    onQuestionUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const handleAddOption = () => {
    const newOption: Option = {
      id: generateId(),
      label: t('Option {{number}}', {number: (question.options?.length || 0) + 1})
    };
    onQuestionUpdate({
      ...question,
      options: [...(question.options || []), newOption]
    });
  };

  const handleDeleteOption = (optionId: string) => {
    const updatedOptions = (question.options || []).filter(option => option.id !== optionId);
    onQuestionUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const options = question.options || [];

  return (
    <Stack
      onClick={onSelect}
      sx={{
        p: 2,
        borderRadius: 1,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          backgroundColor: 'action.hover'
        },
        position: 'relative'
      }}
    >
      <Box sx={{alignSelf: 'flex-end'}}>
        <FormControlLabel
          control={
            <Switch
              checked={question.required || false}
              onChange={handleRequiredChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
          }
          label={<Typography variant="caption">{t('Required')}</Typography>}
          labelPlacement="start"
          onClick={(e) => e.stopPropagation()}
          sx={{m: 0}}
        />
      </Box>

      <TextField
        fullWidth
        label={t('Question Label')}
        value={question.title}
        onChange={handleTitleChange}
        variant="standard"
        sx={{mb: 2, pr: 12}}
        onClick={(e) => e.stopPropagation()}
      />

      {isSelected ? (
        <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
          <MdEditor
            value={question.description || ''}
            onChange={handleDescriptionChange}
            placeholder={t('Question Description (optional)')}
            className={'mini-mdx'}
          />
        </Box>
      ) : (
        question.description && (
          <Box sx={{mb: 2}}>
            <MdRenderer>
              {question.description}
            </MdRenderer>
          </Box>
        )
      )}

      <Select
        fullWidth
        disabled
        displayEmpty
        value=""
        variant="outlined"
        size="small"
        sx={{mb: 2}}
      >
        <MenuItem value="" disabled>
          {t('Select an option')}
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {isSelected && (
        <Box sx={{mt: 2}}>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
            {t('Options')}
          </Typography>
          {options.map((option, index) => (
            <Box key={option.id} sx={{display: 'flex', alignItems: 'center', mb: 1}}>
              <TextField
                fullWidth
                value={option.label}
                onChange={(e) => handleOptionChange(option.id, e.target.value)}
                variant="outlined"
                size="small"
                placeholder={t('Option {{number}}', {number: index + 1})}
                onClick={(e) => e.stopPropagation()}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteOption(option.id);
                }}
                sx={{ml: 1}}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'primary.main',
              cursor: 'pointer',
              mt: 1,
              '&:hover': {opacity: 0.8}
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddOption();
            }}
          >
            <AddIcon fontSize="small" sx={{mr: 0.5}} />
            <Typography variant="body2">{t('Add option')}</Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default DropdownInput;
